import {
  CardHeader,
  Flex,
  ToggleButtonGroup,
  ToggleButton,
  CardSection,
  Table,
  Card,
  Spinner,
  Button,
  Gleam,
  IconFilters,
  IconButton,
} from '@zillow/constellation';
import { useGetTasksByOrderIdQuery } from 'API/order/OrderTaskAPI';
import { useMyUserId } from 'components/accessControl/hooks/hooks';
import { AddTaskRow } from 'components/tasks/AddTaskRow';
import { CompletedTaskHeaders } from 'components/tasks/CompletedTaskHeaders';
import { CompletedTaskRows } from 'components/tasks/CompletedTaskRows';
import { sortTasksBy } from 'components/tasks/sortHelpers';
import { TaskHeaders } from 'components/tasks/TaskHeaders';
import { TaskRows } from 'components/tasks/TaskRows';
import React, { FormEvent, FormEventHandler, useMemo, useState } from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';
import styled from 'styled-components';
import { assertDefined } from 'utilities/Assertions';
import { Button as SpruceButton } from '../../ui-toolkit/components/Button/Button';
import { ExtraFilters } from './ExtraFilters';
import {
  useGetPersonFilterQuery,
  useGetRoleFilterQuery,
  usePutPersonFilterMutation,
  usePutRoleFiltersMutation,
} from 'API/personalization/PersonalizationApi';

const StyledCard = styled(Card)({
  width: '100%',
  height: '100%',
  minHeight: '340px',
  border: 'none',
  minWidth: '700px',
});

interface ITasksContainerProps {
  dialogControls?: React.ReactNode;
  maxCount?: number;
  highlightedTaskId?: string;
  defaultTab?: 'Mine' | 'All' | 'Completed';
  showFiltersByDefault?: boolean;
}

export const TasksContainer: React.FC<ITasksContainerProps> = ({
  maxCount,
  dialogControls,
  highlightedTaskId,
  defaultTab = 'Mine',
  showFiltersByDefault = false,
}) => {
  const [{ column, direction }, setSort] = useState<{
    column: 'dueDate' | 'assignedTo' | 'role' | 'taskName';
    direction: 'ascending' | 'descending' | 'none';
  }>({ column: 'dueDate', direction: 'ascending' });
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [tabFilter, setTabFilter] = useState<'Mine' | 'All' | 'Completed'>(defaultTab);
  const { data: usersFilter = [] } = useGetPersonFilterQuery();
  const [setUsersFilter] = usePutPersonFilterMutation();
  const { data: rolesFilter = [] } = useGetRoleFilterQuery();
  const [setRolesFilter] = usePutRoleFiltersMutation();
  const [showExtraFilters, setShowExtraFilters] = useState(showFiltersByDefault);
  const onFilterChange: FormEventHandler<HTMLDivElement> & ((value: string) => void) = (
    value: string | FormEvent<HTMLDivElement>
  ) => {
    if (value === 'Mine' || value === 'All' || value === 'Completed') {
      setTabFilter(value);
    }
  };

  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const { data: orderTasks = [], isLoading } =
    useGetTasksByOrderIdQuery({ orderId }, { refetchOnMountOrArgChange: true });

  const userId = useMyUserId();
  const filteredTasks = useMemo(
    () =>
      orderTasks
        .filter((task) => {
          if (tabFilter === 'Mine') {
            return task.teammate?.id === userId;
          }
          if (usersFilter.length > 0) {
            return usersFilter.includes(task.teammate?.id ?? '');
          }
          return true;
        })
        .filter((task) => {
          if (tabFilter === 'Mine') {
            return true;
          }
          if (rolesFilter.length > 0 && task.role) {
            return rolesFilter.includes(task.role);
          }
          return true;
        })
        .filter((task) => {
          return (tabFilter === 'Completed') === task.completed;
        })
        .toSorted(sortTasksBy(column, direction, tabFilter)),
    [orderTasks, tabFilter, column, direction, usersFilter, rolesFilter, userId],
  );

  const myTaskCount = orderTasks.filter((t) => t.teammate?.id === userId && !t.completed).length;
  const allTaskCount = orderTasks.filter((t) => !t.completed).length;
  const completedTaskCount = orderTasks.filter((t) => t.completed).length;
  const tasksToShow = useMemo(() => {
    return maxCount === undefined ? filteredTasks : filteredTasks.slice(0, maxCount);
  }, [filteredTasks, maxCount]);
  const canShowExtraFilter = tabFilter !== 'Mine';
  const isShowingExtraFilters = showExtraFilters && canShowExtraFilter;
  const filterCount = usersFilter.length + rolesFilter.length;

  return (
    <StyledCard cardType={'secondary'} cardElevation={1}>
      <CardHeader marginBottom={0}>
        <Flex display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
          <Flex display={'flex'} alignItems={'center'} gap={1}>
            {dialogControls}
            {isAddingNew && tabFilter !== 'Completed' && <>Add New Task</>}
            {tabFilter === 'Completed' && <>Completed Tasks</>}
            {!isAddingNew && tabFilter !== 'Completed' && <>Tasks</>}
            {filterCount > 0 && canShowExtraFilter && (
              <Gleam count={usersFilter.length + rolesFilter.length} maxSuffix maxCount={10}>
                <IconButton
                  icon={<IconFilters />}
                  size={'sm'}
                  title={'Filter Tasks'}
                  bare
                  buttonType="secondary"
                  onClick={() => setShowExtraFilters(!showExtraFilters)}
                  disabled={!canShowExtraFilter}
                ></IconButton>
              </Gleam>
            )}
            {(filterCount === 0 || !canShowExtraFilter) && (
              <IconButton
                icon={<IconFilters />}
                size={'sm'}
                title={'Filter Tasks'}
                bare
                buttonType="secondary"
                onClick={() => setShowExtraFilters(!showExtraFilters)}
                disabled={!canShowExtraFilter}
              ></IconButton>
            )}
          </Flex>
          <Flex display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
            {!isAddingNew && tabFilter !== 'Completed' && (
              <>
                {!isZillowClosingEnabled ? (
                  <div style={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}>
                    <SpruceButton onClick={() => setIsAddingNew(!isAddingNew)} intent={'primary'}>
                      Add New
                    </SpruceButton>
                  </div>
                ) : (
                  <Button onClick={() => setIsAddingNew(!isAddingNew)} size={'sm'} buttonType={'primary'}>
                    Add New
                  </Button>
                )}
              </>
            )}
            <ToggleButtonGroup value={tabFilter} onChange={onFilterChange} aria-label="Filter Tasks">
              <ToggleButton value={'Mine'}>
                Mine&nbsp;
                <Gleam
                  count={myTaskCount}
                  maxCount={100}
                  maxSuffix
                  appearance={myTaskCount === 0 ? 'new' : 'default'}
                ></Gleam>
              </ToggleButton>
              <ToggleButton value={'All'}>
                All&nbsp;
                <Gleam
                  count={allTaskCount}
                  maxCount={100}
                  maxSuffix
                  appearance={allTaskCount === 0 ? 'new' : 'default'}
                ></Gleam>
              </ToggleButton>
              <ToggleButton value={'Completed'}>
                Completed&nbsp;<Gleam count={completedTaskCount} maxCount={100} maxSuffix appearance={'info'}></Gleam>
              </ToggleButton>
            </ToggleButtonGroup>
          </Flex>
        </Flex>
      </CardHeader>
      <CardSection padding={0} margin={0} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            {isShowingExtraFilters && (
              <ExtraFilters
                flexDirection={(maxCount ?? 0) > 0 ? 'column' : 'row'}
                onChangeUsersFilter={setUsersFilter}
                usersFilter={usersFilter}
                onChangeRolesFilter={setRolesFilter}
                rolesFilter={rolesFilter}
              />
            )}
            <Table.Sortable
              defaultSortDirections={[null, 'none', 'none', 'none', 'ascending', null]}
              onSortChange={({ nextSortDirection }, props) =>
                setSort({ column: props.id, direction: nextSortDirection })
              }
            >
              <Table appearance={'zebra'} aria-label={'Quick Task List'} size="sm">
                <Table.Header appearance={'dark'}>
                  {tabFilter !== 'Completed' && <TaskHeaders />}
                  {tabFilter === 'Completed' && <CompletedTaskHeaders />}
                </Table.Header>
                <Table.Body>
                  {isAddingNew && tabFilter !== 'Completed' && <AddTaskRow onComplete={() => setIsAddingNew(false)} />}
                  {tabFilter !== 'Completed' && <TaskRows tasks={tasksToShow} highlightedTaskId={highlightedTaskId} />}
                  {tabFilter === 'Completed' && <CompletedTaskRows tasks={tasksToShow} />}
                </Table.Body>
              </Table>
            </Table.Sortable>
          </>
        )}
      </CardSection>
    </StyledCard>
  );
};
